// Clips from community (docs/backend/community.md, "Clips"): a short recording of a game moment. The site shows the
// video from the clip's signed `file_url`, the game and the moment, and "Play this" opens the launcher at the game
// (thrdm://play/<g_id>). Nothing here records or uploads: the launcher does that.

import { api, ApiError } from "./api.ts";
import { gameById, getCatalogue, isComingSoon } from "./catalogue.ts";
import { actionButton } from "./components.ts";
import { clear, h, link } from "./dom.ts";
import { downloadPath, launch, playLink } from "./play.ts";
import { navigate } from "./router.ts";
import { currentMe, signedIn } from "./session.ts";

export interface Clip {
  id: string;
  game_id: string;
  version: string | null;
  moment_seconds: number;
  uploader_id: string;
  title: string | null;
  status: "uploading" | "visible" | "hidden" | "removed";
  created_at: number;
  hosted: boolean;
  duration_seconds: number | null;
  duration_verified: boolean | null;
  size: number | null;
  content_type: string | null;
  external_url: string | null;
  file_url: string | null;
  /** client-supplied poster image (backend wave 4); absent on older community builds */
  thumbnail_url?: string | null;
}

export interface ClipGame {
  id: string;
  title: string | null;
  play: string;
}

export interface FeedItem {
  clip: Clip;
  reasons: ("friend" | "game" | "creator")[];
  game: ClipGame;
}

export interface FeedPage {
  items: FeedItem[];
  next_cursor: string | null;
  degraded: string[];
}

export const CLIP_ID = /^cl_[0-9A-Za-z]{10,40}$/;
const GAME_ID = /^g_[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const clipPath = (id: string): string => `/clip/${encodeURIComponent(id)}`;

/** 93.5 → "1:33", 3725 → "1:02:05". The moment is seconds into the player's session. */
export function fmtMoment(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return hh ? `${hh}:${String(mm).padStart(2, "0")}:${ss}` : `${mm}:${ss}`;
}

/** "12 s", "1 min 5 s" for a clip's length. */
export function fmtLength(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ""}`;
}

/** "3 minutes ago", "yesterday", or the date. `now` in unix seconds. */
export function fmtAgo(at: number, now = Date.now() / 1000): string {
  const d = Math.max(0, now - at);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)} min ago`;
  if (d < 86400) return `${Math.floor(d / 3600)} h ago`;
  if (d < 2 * 86400) return "yesterday";
  if (d < 30 * 86400) return `${Math.floor(d / 86400)} days ago`;
  return new Date(at * 1000).toISOString().slice(0, 10);
}

/** Only http(s) URLs reach a src attribute: the API's signed link, or a poster. */
export function safeMediaUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  try {
    const url = new URL(u, location.href);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

/** "Play this": the launcher deep link for the clip's game, from the id we validate (never a URL from the API). */
export function clipPlayLink(gameId: string): string | null {
  return GAME_ID.test(gameId) ? playLink(gameId) : null;
}

export const REASON_LABEL: Record<FeedItem["reasons"][number], string> = {
  friend: "From a friend",
  game: "A game you follow",
  creator: "A creator you follow",
};

export const DEGRADED_LABEL: Record<string, string> = {
  blocks: "Some clips are held back because we couldn't check blocks just now.",
  friends: "Clips from friends are missing right now.",
  following: "Clips from people you follow are missing right now.",
  follows: "Clips from games and creators you follow are missing right now.",
  creator_games: "Some clips from creators you follow are missing right now.",
  followed_creators: "Some clips from creators you follow are missing right now.",
};

/** The degraded notes to show (each once), for whatever community says it couldn't load. */
export function degradedNotes(degraded: string[]): string[] {
  return [...new Set(degraded.map((d) => DEGRADED_LABEL[d] ?? "Part of your feed couldn't be loaded just now."))];
}

/** The video element: the signed file link, the poster when present, metadata only until played. */
export function clipVideo(c: Clip, opts: { autoplay?: boolean } = {}): HTMLElement {
  const src = safeMediaUrl(c.file_url);
  const poster = safeMediaUrl(c.thumbnail_url ?? null);
  if (!src) {
    const ext = safeMediaUrl(c.external_url);
    return h(
      "div",
      { class: "clip-video clip-novideo", style: poster ? { backgroundImage: `url("${poster}")` } : undefined },
      ext && ext.startsWith("https:") ? h("a", { href: ext, rel: "noopener noreferrer", target: "_blank", class: "btn ghost small" }, "Watch where it was posted") : h("span", { class: "dim" }, "No video for this clip."),
    );
  }
  const v = h("video", {
    class: "clip-video",
    // without a poster, a media fragment makes the browser show a frame instead of a black box
    src: poster || src.includes("#") ? src : `${src}#t=0.1`,
    poster: poster ?? undefined,
    controls: true,
    playsinline: true,
    preload: opts.autoplay ? "auto" : "metadata",
    autoplay: opts.autoplay ? true : undefined,
    muted: opts.autoplay ? true : undefined,
    "aria-label": c.title ? `Clip: ${c.title}` : "Clip",
  });
  return v;
}

/** "Play this" for a clip's game: the deep link, falling back to the download page. */
export function playThis(gameId: string, title: string | null, size: "big" | "small" = "small"): HTMLElement | null {
  const href = clipPlayLink(gameId);
  if (!href) return null;
  const el = playThisLink(gameId, href, title, size);
  // a coming-soon game has nothing to play yet: swap in "Notify me in Banger" once the catalogue says so
  void getCatalogue()
    .then((cat) => {
      const g = gameById(cat, gameId);
      if (g && isComingSoon(g)) el.replaceWith(actionButton(g, "notify", "Notify me in Banger", `btn${size === "small" ? " small" : ""}`));
    })
    .catch(() => {});
  return el;
}

function playThisLink(gameId: string, href: string, title: string | null, size: "big" | "small"): HTMLElement {
  return h(
    "a",
    {
      class: `btn-play ${size}`,
      href,
      "aria-label": `Play ${title ?? "this game"} in the Banger app`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(gameId, { onMissing: () => navigate(downloadPath(gameId)) });
      },
    },
    h("span", { "aria-hidden": "true", class: "tri" }, "▶"),
    "Play this",
  );
}

/** A clip in a list: the video, the game, the moment, and a link to the clip's page. */
export function clipCard(c: Clip, game: ClipGame | null, extra: { reasons?: FeedItem["reasons"]; showReport?: boolean } = {}): HTMLElement {
  const title = game?.title ?? null;
  return h(
    "li",
    { class: "clip-card" },
    clipVideo(c),
    h(
      "div",
      { class: "clip-meta" },
      h("a", { href: clipPath(c.id), "data-link": true, class: "clip-title" }, c.title || (title ? `A moment in ${title}` : "A clip")),
      h(
        "p",
        { class: "dim small" },
        game ? link(`/g/${game.id}`, null, title ?? "This game") : null,
        game ? " · " : null,
        `at ${fmtMoment(c.moment_seconds)}`,
        c.version ? ` · v${c.version}` : null,
        ` · ${fmtAgo(c.created_at)}`,
      ),
      extra.reasons?.length ? h("p", { class: "small clip-why" }, extra.reasons.map((r) => REASON_LABEL[r] ?? r).join(" · ")) : null,
      h("div", { class: "clip-actions" }, game ? playThis(game.id, title) : null, extra.showReport !== false ? reportButton(c) : null),
    ),
  );
}

export const REPORT_REASONS: [string, string][] = [
  ["spam", "Spam"],
  ["harassment", "Harassment or bullying"],
  ["hate", "Hate"],
  ["abuse", "Abusive or sexual content"],
  ["cheating", "Cheating"],
  ["spoilers", "Spoilers"],
  ["other", "Something else"],
];

/** Report a clip to the moderators. Not shown on your own clips (community refuses them with own_content). */
export function reportButton(c: Clip): HTMLElement | null {
  const me = currentMe();
  if (me && me.id === c.uploader_id) return null;
  const host = h("div", { class: "report" });
  const open = () => {
    if (!signedIn()) {
      navigate(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    const reason = h("select", { name: "reason", "aria-label": "Reason" }, REPORT_REASONS.map(([v, t]) => h("option", { value: v }, t)));
    const note = h("input", { name: "note", maxlength: 500, placeholder: "Anything moderators should know (optional)", "aria-label": "Note" });
    const msg = h("div", { "aria-live": "polite" });
    clear(
      host,
      h(
        "form",
        {
          class: "report-form",
          onsubmit: async (e: Event) => {
            e.preventDefault();
            try {
              const r = await sendReport(c.id, (reason as HTMLSelectElement).value, (note as HTMLInputElement).value.trim());
              clear(host, h("p", { class: "small dim", role: "status" }, r.duplicate ? "You've already reported this clip. Moderators will look at it." : "Thanks. Moderators will look at it."));
            } catch (x) {
              clear(msg, h("p", { class: "error small", role: "alert" }, reportError(x)));
            }
          },
        },
        reason,
        note,
        h("div", { class: "actions" }, h("button", { class: "btn small", type: "submit" }, "Send report"), h("button", { class: "btn small ghost", type: "button", onclick: () => clear(host, button) }, "Cancel")),
        msg,
      ),
    );
  };
  const button = h("button", { type: "button", class: "btn small ghost", onclick: open }, "Report");
  clear(host, button);
  return host;
}

export async function sendReport(clipId: string, reason: string, note: string): Promise<{ id: string; duplicate: boolean }> {
  const r = await api<{ report: { id: string; duplicate: boolean } }>("community", "/reports", {
    body: { target_type: "clip", target_id: clipId, reason, ...(note ? { note } : {}) },
  });
  return r.report;
}

export function reportError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "own_content") return "That's your own clip.";
    if (e.code === "rate_limited") return "You've sent a lot of reports. Please wait a little and try again.";
    if (e.code === "not_found" || e.code === "clip_not_found") return "That clip isn't available any more.";
  }
  return e instanceof Error ? e.message : String(e);
}

/** A clip page's load error, in plain words. */
export function clipError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return "This clip isn't available. It may have been removed, or it isn't visible to you.";
    if (e.status === 503) return "We couldn't load this clip just now. Please try again in a moment.";
  }
  return e instanceof Error ? e.message : String(e);
}

